<template>
    <div>
        <CicPopup
            v-if="!saving"
            id="frmAccessNumberDetails"
            :saveable="$store.hasRight('EDIT_INBOUND_NUMBERS') && !$store.showLoadingOverlay"
            title="Access Number Details"
            @blur="$emit('blur')"
            @input="validateItem"
        >
            <div class="access-title">{{ $t('Access Point [x]').replace(/\[x]/g, item.name) }}</div>
            <div class="access-sub-title">{{ $t('DESC_ACCESS_NUMBER_DETAILS') }}</div>
            <ul class="item-form">
                <template v-if="$store.hasRight('EDIT_LOOPING') && item.type === 1">
                    <li class="form-label">{{ $t('Looping to Access Number') }}</li>
                    <li>
                        <CicSelect
                            id="access_looping"
                            :enabled="editing"
                            :value="item.looping"
                            :options="[
                                { value: 0, label: $t('Use Organisation Setting') },
                                { value: 1, label: $t('Prevent Looping') },
                                { value: 2, label: $t('Allow Looping') },
                                {
                                    value: 3,
                                    label: $t('Allow Loop except on outbound DDI matching the inbound Access Number'),
                                },
                            ]"
                            @input="(val) => updateFieldValue('looping', val)"
                        />
                    </li>
                </template>
                <li class="form-label inline-form-item">
                    <span>{{ $t('Version') }}</span>
                    <IconButton :title="$t('DESC_ACCESS_VERSION')" icon="help-icon" />
                </li>
                <li id="version_select">
                    <span>{{ item.revision }}</span>
                    <IconButton
                        v-if="editing"
                        :title="$t('Edit Access Number Version')"
                        icon="revisions-icon"
                        @click="showRevisions = true"
                    />
                </li>
                <li class="form-label">{{ $t('Description') }}</li>
                <li>
                    <CicTextbox
                        id="access_description"
                        :enabled="editing"
                        :value="item.description"
                        :maxlength="255"
                        @input="(val) => updateFieldValue('description', val)"
                    />
                </li>
                <template v-if="useOriginationGroups || item.originationGroupId > 0">
                    <li class="form-label">{{ $t('LABEL_SELECT_CLI_GROUP') }}</li>
                    <li>
                        <CicSelect
                            id="access_group"
                            :enabled="editing && useOriginationGroups"
                            :value="item.originationGroupId"
                            :options="
                                [{ value: 0, label: $t('Default') }].concat(
                                    originationGroups.map((group) => ({ value: group.groupId, label: group.name })),
                                )
                            "
                            @input="(val) => updateFieldValue('originationGroupId', val)"
                        />
                    </li>
                </template>
                <li class="form-label inline-form-item">
                    <span>{{ $t('LABEL_ACTIVATE_DR') }}</span>
                    <IconButton :title="$t('DESC_ACTIVATE_DR')" icon="help-icon" />
                </li>
                <li>
                    <CicCheckbox
                        id="access_dr_active"
                        :enabled="editing && item.drPlanId !== 0"
                        :value="item.drActive && item.drPlanId !== 0"
                        @input="(val) => updateFieldValue('drActive', val)"
                    />
                </li>
            </ul>
            <ElTabs :before-leave="switchTab" stretch>
                <ElTabPane :label="$t('Primary')">
                    <EditRoute
                        :org-id="item.orgId"
                        :plan-id="item.planId"
                        :plans="plans"
                        :route="primary"
                        @change="updatePrimaryFieldValue"
                        @change-list="loadPrompts"
                        @input="(planId, revision) => loadPlan(planId, false, revision)"
                    />
                </ElTabPane>
                <ElTabPane :label="$t('DR')">
                    <EditRoute
                        :org-id="item.orgId"
                        :plan-id="item.drPlanId"
                        :plans="plans"
                        :route="dr"
                        @change="updateDRFieldValue"
                        @change-list="loadPrompts"
                        @input="(planId, revision) => loadPlan(planId, true, revision)"
                    />
                </ElTabPane>
            </ElTabs>
        </CicPopup>

        <EditRevision v-if="showRevisions" :item="item" @blur="showRevisions = false" @change="$emit('change')" />
    </div>
</template>

<script>
    import { ElTabs, ElTabPane } from 'element-plus';
    import CicPopup from '@/components/CicPopup';
    import IconButton from '@/components/controls/IconButton';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import { post, load, save } from '@/utils/Http';
    import { showValidationMessage } from '@/utils/Notification';
    import {
        getAccessPointWarning,
        loadInitialPrompts,
        loadPrompts,
        validateRoute,
    } from '@/utils/RouteCollectionUtils';
    import EditRevision from './EditRevision';
    import EditRoute from './EditRoute';
    import '../resources/styles/editAccessPoint.scss';

    export default {
        name: 'EditAccessPoint',
        components: {
            EditRevision,
            CicCheckbox,
            CicPopup,
            CicSelect,
            CicTextbox,
            EditRoute,
            ElTabs,
            ElTabPane,
            IconButton,
        },
        props: {
            accessPoint: {
                type: Object,
                required: true,
            },
        },
        emits: ['blur', 'change'],
        data() {
            return {
                dr: null,
                editing: false,
                item: {},
                originationGroups: [],
                plans: [],
                primary: null,
                saving: false,
                showRevisions: false,
            };
        },
        computed: {
            useOriginationGroups() {
                return [0, 1, 2, 4].includes(this.item.type) && this.$store.hasRight('VIEW_CLI_GROUPS');
            },
        },
        created() {
            this.editing = this.$store.hasRight('EDIT_INBOUND_NUMBERS');
            this.item = JSON.parse(JSON.stringify(this.accessPoint));
            load(
                'routeplans/get',
                {
                    orgId: this.item.orgId,
                    accessId: this.accessPoint.accessId,
                    groupId: this.accessPoint.originationGroupId,
                },
                (response) => {
                    this.plans = response.plans;

                    this.loadPlan(this.item.planId);
                },
            );
            this.originationGroups =
                this.item.originationGroupId > 0
                    ? [{ groupId: this.item.originationGroupId, name: this.item.originationName }]
                    : [];
            if (this.useOriginationGroups) {
                load('originationgroups/get', { orgId: this.item.orgId }, (response) => {
                    this.originationGroups = response.groups.filter(
                        (group) =>
                            (group.type === 'Email' && [0, 4].includes(this.item.type)) ||
                            (group.type !== 'Email' && [0, 1, 2].includes(this.item.type)),
                    );
                });
            }
        },
        methods: {
            confirmText(obj) {
                return this.$t(obj.index === 1 ? 'ACCESS_WARN_SCRIPT_PRIMARY' : 'ACCESS_WARN_SCRIPT_DR').replace(
                    '[x]',
                    obj.cells.join(', '),
                );
            },
            loadPlan(planId, dr = false, revisionId = null) {
                if (dr) {
                    this.dr = null;
                    this.item.drPlanId = planId;
                } else {
                    this.primary = null;
                    this.item.planId = planId;
                }

                if (planId !== 0) {
                    const params = {
                        orgId: this.item.orgId,
                        planId,
                        revisionId,
                        services:
                            this.item.orgId === this.$store.routeData.orgId && this.$store.routeData.mediaLists
                                ? '0'
                                : '1',
                    };
                    const plan = this.plans.find((plan) => plan.planId === planId);
                    load('routeplan/revision', params, (response) => {
                        const data = {
                            planId: planId > 0 ? planId : null,
                            name: planId > 0 ? plan.name : '',
                            revision: revisionId ? revisionId : planId > 0 ? plan.revision : 0,
                            reusable: planId > 0 ? plan.reusable : true,
                            rejectCLIR: planId > 0 ? plan.revisions[0].rejectCLIR : false,
                            withholdCLI: planId > 0 ? plan.revisions[0].withholdCLI : false,
                            route: response.revision,
                            revisions: null,
                            voiceOnly: response.voiceOnly,
                        };

                        if (response.mediaLists) {
                            this.$store.buildRouteStore(response, this.item.orgId);
                        }

                        loadInitialPrompts(data.route, load, this.$forceUpdate, this.item.orgId);

                        if (dr) {
                            this.dr = data;
                            this.item.drPlanId = planId;
                        } else {
                            this.primary = data;
                            this.item.planId = planId;
                        }
                    });
                }
            },
            loadPrompts(mediaListId) {
                loadPrompts(mediaListId, post, this.$forceUpdate, this.item.orgId);
            },
            saveItem(confirmed = false) {
                this.saving = true;
                const params = {
                    accessId: this.item.accessId,
                    orgId: this.item.orgId,
                    looping: this.item.looping,
                    originationGroupId: this.item.originationGroupId,
                    description: this.item.description,
                    planId: this.item.planId,
                    drPlanId: this.item.drPlanId,
                    drActive: this.item.drActive ? 1 : 0,
                    confirmed: confirmed ? 1 : 0,
                    primary: this.primary
                        ? JSON.stringify({
                              name: this.primary.name,
                              reusable: this.primary.reusable,
                              rejectCLIR: this.primary.rejectCLIR,
                              withholdCLI: this.primary.withholdCLI,
                              ...this.primary.route,
                          })
                        : null,
                    dr: this.dr
                        ? JSON.stringify({
                              name: this.dr.name,
                              reusable: this.dr.reusable,
                              rejectCLIR: this.dr.rejectCLIR,
                              withholdCLI: this.dr.withholdCLI,
                              ...this.dr.route,
                          })
                        : null,
                };
                save('accesspoint/updateroutes', params, () => {
                    this.$emit('change');
                }).then((response) => {
                    this.saving = false;
                    if (
                        response &&
                        response.status === 'confirm' &&
                        confirm(this.confirmText(response.cells[0])) &&
                        (response.cells.length === 1 || confirm(this.confirmText(response.cells[1])))
                    ) {
                        this.saveItem(true);
                    }
                });
            },
            switchTab() {
                if (this.item.drPlanId !== 0 && !this.dr) {
                    this.loadPlan(this.item.drPlanId, true);
                }
            },
            updateDRFieldValue(name, value) {
                this.dr[name] = value;
            },
            updateFieldValue(name, value) {
                this.item[name] = value;
            },
            updatePrimaryFieldValue(name, value) {
                this.primary[name] = value;
            },
            validateItem() {
                const planIds = [];
                this.confirmed = false;
                if (
                    !this.validatePlan(this.item.planId, this.primary, planIds) ||
                    !this.validatePlan(this.item.drPlanId, this.dr, planIds)
                ) {
                    return;
                }

                this.saving = true;
                let warning = this.$t('ACCESS_WARN_NO_POINTS');
                if (planIds.length > 0) {
                    load(
                        'routeplan/accesspoints',
                        { orgId: this.item.orgId, planIds, accessId: this.item.accessId },
                        (response) => {
                            let key = 'ACCESS_WARN_NO_POINTS';
                            const messages = [null];
                            if (this.item.planId > 0) {
                                messages[0] = response.messages.find((message) => message.planId === this.item.planId);
                                if (messages[0].total > 0) {
                                    key = 'ACCESS_WARN_POINTS_PRIMARY';
                                }
                            }
                            if (this.item.drPlanId > 0) {
                                const dr = response.messages.find((message) => message.planId === this.item.drPlanId);
                                messages.push(dr);
                                if (dr.total > 0) {
                                    key =
                                        messages[0] && messages[0].total > 0
                                            ? 'ACCESS_WARN_POINTS_BOTH'
                                            : 'ACCESS_WARN_POINTS_DR';
                                }
                            }

                            warning = getAccessPointWarning(this.$t(key), messages);
                            if (confirm(warning)) {
                                this.saveItem();
                            } else {
                                this.saving = false;
                            }
                        },
                    ).then((response) => {
                        if (!response || response.status !== 'OK') {
                            this.saving = false;
                        }
                    });
                } else {
                    this.saveItem();
                }
            },
            validatePlan(planId, plan, planIds) {
                if (planId !== 0) {
                    if (plan) {
                        if (plan.name.trim() === '') {
                            showValidationMessage('Route Plan Name is a required field');
                            return false;
                        }
                        if (!validateRoute(plan.route)) {
                            return false;
                        }
                    }
                    if (planId > 0) {
                        planIds.push(planId);
                    }
                }

                return true;
            },
        },
    };
</script>
